import { supabase } from "@/integrations/supabase/client";
import { Person, PaginatedResponse, BookSummary } from "@/types";

export const fetchPeople = async (
  page: number = 1,
  pageSize: number = 10,
  filters: {
    search?: string;
    roleId?: number;
  } = {}
): Promise<PaginatedResponse<Person>> => {
  // Build the query based on whether we're filtering by role
  let query;

  // If roleId is provided, convert it to a number to ensure type consistency
  let roleIdNumber: number | undefined;
  if (filters.roleId) {
    roleIdNumber = Number(filters.roleId);
    console.log(
      "Filtering by role ID:",
      filters.roleId,
      typeof filters.roleId,
      roleIdNumber
    );
  }

  if (roleIdNumber) {
    // Use inner join when filtering by role
    query = supabase
      .from("people")
      .select(
        `
      *,
      roles:people_to_roles!inner(
        role:people_roles(*)
      )
    `
      )
      .eq("roles.role_id", roleIdNumber);
  } else {
    // Normal join when not filtering by role
    query = supabase.from("people").select(`
      *,
      roles:people_to_roles(
        role:people_roles(*)
      )
    `);
  }

  // Apply search filter if provided
  if (filters.search) {
    query = query.or(
      `name.ilike.%${filters.search}%,email.ilike.%${filters.search}%`
    );
  }

  // Execute count query separately
  let countQuery;

  if (roleIdNumber) {
    // When filtering by role, we need to join for the count as well
    countQuery = supabase
      .from("people")
      .select(
        `
        id,
        roles:people_to_roles!inner(role_id)
      `,
        { count: "exact", head: true }
      )
      .eq("roles.role_id", roleIdNumber);
  } else {
    // Simple count when not filtering by role
    countQuery = supabase
      .from("people")
      .select("id", { count: "exact", head: true });

    // Apply search filter to count query if needed
    if (filters.search) {
      countQuery.or(
        `name.ilike.%${filters.search}%,email.ilike.%${filters.search}%`
      );
    }
  }

  const { count, error: countError } = await countQuery;

  if (countError) {
    console.error("Error fetching people count:", countError);
    throw countError;
  }

  // Calculate pagination
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  // Execute query with pagination
  const { data, error } = await query.range(from, to).order("name");

  if (error) {
    console.error("Error fetching people:", error);
    throw error;
  }

  // Transform the data to match our Person type
  const people: Person[] =
    data?.map((person) => {
      const roles =
        person.roles?.map((item) => ({
          id: item.role.id,
          name: item.role.name,
          description: item.role.description,
          isActive: item.role.is_active,
          createdAt: item.role.created_at,
          updatedAt: item.role.updated_at,
        })) || [];

      return {
        id: person.id,
        name: person.name,
        email: person.email,
        bio: person.bio,
        avatarUrl: person.avatar_url,
        roles: roles,
        createdAt: person.created_at,
        updatedAt: person.updated_at,
      };
    }) || [];

  // Fetch book counts for all people at once (optimized - 3 queries instead of 3 per person)
  const personIds = people.map((p) => p.id);
  
  if (personIds.length > 0) {
    // Single query to get all author book IDs
    const { data: authorBooks } = await supabase
      .from("books_authors")
      .select("person_id, book_id")
      .in("person_id", personIds);

    // Single query to get all narrator book IDs
    const { data: narratorBooks } = await supabase
      .from("summary_contents")
      .select("audio_narrator, book_id")
      .in("audio_narrator", personIds)
      .not("audio_narrator", "is", null);

    // Single query to get all translator book IDs
    const { data: translatorBooks } = await supabase
      .from("summary_contents")
      .select("translator, book_id")
      .in("translator", personIds)
      .not("translator", "is", null);

    // Aggregate book counts per person
    const bookCountsMap = new Map<number, Set<number>>();
    
    // Initialize map for all people
    personIds.forEach((id) => {
      bookCountsMap.set(id, new Set<number>());
    });

    // Add author books
    authorBooks?.forEach((item) => {
      const bookSet = bookCountsMap.get(item.person_id);
      if (bookSet) {
        bookSet.add(item.book_id);
      }
    });

    // Add narrator books
    narratorBooks?.forEach((item) => {
      const bookSet = bookCountsMap.get(item.audio_narrator);
      if (bookSet) {
        bookSet.add(item.book_id);
      }
    });

    // Add translator books
    translatorBooks?.forEach((item) => {
      const bookSet = bookCountsMap.get(item.translator);
      if (bookSet) {
        bookSet.add(item.book_id);
      }
    });

    // Assign book counts to each person
    people.forEach((person) => {
      const bookSet = bookCountsMap.get(person.id);
      const bookCount = bookSet ? bookSet.size : 0;
      // Store count for display, keep associatedBooks for backward compatibility
      person.bookCount = bookCount;
      person.associatedBooks = bookSet ? Array.from(bookSet) : [];
    });
  } else {
    // No people, set counts to 0
    people.forEach((person) => {
      person.bookCount = 0;
      person.associatedBooks = [];
    });
  }

  // Return data in the PaginatedResponse format
  return {
    data: people,
    total: count || 0,
    page,
    pageSize,
    totalPages: Math.ceil((count || 0) / pageSize),
  };
};

export const fetchPeopleByRole = async (roleId: number): Promise<Person[]> => {
  const { data, error } = await supabase
    .from("people")
    .select(
      `
      *,
      roles:people_to_roles!inner(
        role:people_roles(*)
      )
    `
    )
    .eq("roles.role_id", roleId);

  if (error) {
    console.error("Error fetching people by role:", error);
    throw error;
  }

  // Transform the data to match our Person type
  return (
    data?.map((person) => {
      const roles =
        person.roles?.map((item) => ({
          id: item.role.id,
          name: item.role.name,
          description: item.role.description,
          isActive: item.role.is_active,
          createdAt: item.role.created_at,
          updatedAt: item.role.updated_at,
        })) || [];

      return {
        id: person.id,
        name: person.name,
        email: person.email,
        bio: person.bio,
        avatarUrl: person.avatar_url,
        roles: roles,
        createdAt: person.created_at,
        updatedAt: person.updated_at,
      };
    }) || []
  );
};

export const fetchPerson = async (id: number): Promise<Person> => {
  const { data, error } = await supabase
    .from("people")
    .select(
      `
      *,
      roles:people_to_roles(
        role:people_roles(*)
      )
    `
    )
    .eq("id", id)
    .single();

  if (error) {
    console.error("Error fetching person:", error);
    throw error;
  }

  // Transform the data to match our Person type
  const roles =
    data.roles?.map((item) => ({
      id: item.role.id,
      name: item.role.name,
      description: item.role.description,
      isActive: item.role.is_active,
      createdAt: item.role.created_at,
      updatedAt: item.role.updated_at,
    })) || [];

  // Fetch books where person is an author
  const { data: authorBooks } = await supabase
    .from("books_authors")
    .select("book_id")
    .eq("person_id", id);

  // Fetch books where person is a narrator
  const { data: narratorBooks } = await supabase
    .from("summary_contents")
    .select("book_id")
    .eq("audio_narrator", id);

  // Fetch books where person is a translator
  const { data: translatorBooks } = await supabase
    .from("summary_contents")
    .select("book_id")
    .eq("translator", id);

  // Combine all book IDs
  const bookIds = new Set<number>();

  authorBooks?.forEach((item) => bookIds.add(item.book_id));
  narratorBooks?.forEach((item) => bookIds.add(item.book_id));
  translatorBooks?.forEach((item) => bookIds.add(item.book_id));

  return {
    id: data.id,
    name: data.name,
    email: data.email,
    bio: data.bio,
    avatarUrl: data.avatar_url,
    roles: roles,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
    associatedBooks: Array.from(bookIds),
  };
};

// Type for books with role information
export type BookWithRole = BookSummary & {
  personRole: string;
  roleTypes: {
    isAuthor: boolean;
    isNarrator: boolean;
    isTranslator: boolean;
  };
  bookBio?: string;
};

// Fetch detailed book information for a person with role information
export const fetchPersonBooks = async (personId: number): Promise<BookWithRole[]> => {
  // Fetch books where person is an author (include bio)
  const { data: authorBooks } = await supabase
    .from("books_authors")
    .select("book_id, bio")
    .eq("person_id", personId);

  // Fetch books where person is a narrator
  const { data: narratorBooks } = await supabase
    .from("summary_contents")
    .select("book_id")
    .eq("audio_narrator", personId);

  // Fetch books where person is a translator
  const { data: translatorBooks } = await supabase
    .from("summary_contents")
    .select("book_id")
    .eq("translator", personId);

  // Combine all unique book IDs
  const bookIds = new Set<number>();
  authorBooks?.forEach((item) => bookIds.add(item.book_id));
  narratorBooks?.forEach((item) => bookIds.add(item.book_id));
  translatorBooks?.forEach((item) => bookIds.add(item.book_id));

  if (bookIds.size === 0) {
    return [];
  }

  // Fetch full book details
  const { data: booksData, error: booksError } = await supabase
    .from("books")
    .select(
      `
      *,
      authors:books_authors(
        person:people(id, name)
      ),
      summary_contents(
        id,
        language,
        audio_narrator,
        translator,
        audio_narrator_person:people!summary_contents_audio_narrator_fkey(id, name),
        translator_person:people!summary_contents_translator_fkey(id, name)
      )
    `
    )
    .in("id", Array.from(bookIds));

  if (booksError) {
    console.error("Error fetching books:", booksError);
    throw booksError;
  }

  if (!booksData) {
    return [];
  }

  // Build a map of book_id → bio from author books
  const authorBioMap = new Map<number, string | null>();
  authorBooks?.forEach((item) => {
    authorBioMap.set(item.book_id, item.bio);
  });

  // Transform books and add role information
  const booksWithRoles: BookWithRole[] = booksData.map((book: any) => {
    // Check if person is an author
    const isAuthor = book.authors?.some(
      (author: any) => author.person?.id === personId
    ) || false;

    // Check if person is a narrator or translator in any summary content
    let isNarrator = false;
    let isTranslator = false;

    book.summary_contents?.forEach((content: any) => {
      if (content.audio_narrator === personId) {
        isNarrator = true;
      }
      if (content.translator === personId) {
        isTranslator = true;
      }
    });

    // Build role label
    let roleLabel = "";
    if (isAuthor) roleLabel = "Author";
    if (isNarrator)
      roleLabel = roleLabel ? `${roleLabel}, Narrator` : "Narrator";
    if (isTranslator)
      roleLabel = roleLabel ? `${roleLabel}, Translator` : "Translator";

    // Transform authors
    const authors = book.authors?.map((author: any) => ({
      id: author.person?.id,
      name: author.person?.name || "Unknown",
    })) || [];

    // Transform summary contents
    const summaryContents = book.summary_contents?.map((content: any) => ({
      id: content.id,
      bookId: book.id,
      language: content.language,
      audioNarrator: content.audio_narrator_person
        ? {
            id: content.audio_narrator_person.id,
            name: content.audio_narrator_person.name,
          }
        : undefined,
      translator: content.translator_person
        ? {
            id: content.translator_person.id,
            name: content.translator_person.name,
          }
        : undefined,
      chapters: [], // Not needed for this view
    })) || [];

    return {
      id: book.id,
      title: book.title,
      authors: authors,
      coverImage: book.cover_image || "",
      coverBgColor: book.cover_bg_color || "#E9ECEF",
      categories: [], // Not fetched for performance
      readingTime: book.reading_time || 0,
      createdAt: book.created_at,
      updatedAt: book.updated_at,
      rating: book.rating || 0,
      viewCount: book.view_count || 0,
      summaryContents: summaryContents,
      isActive: book.is_active ?? true,
      isFree: book.is_free ?? false,
      personRole: roleLabel || "Unknown",
      roleTypes: {
        isAuthor,
        isNarrator,
        isTranslator: isTranslator,
      },
      bookBio: isAuthor ? (authorBioMap.get(book.id) ?? undefined) : undefined,
    };
  });

  return booksWithRoles;
};

export const updateBookAuthorBio = async (
  bookId: number,
  personId: number,
  bio: string | null
): Promise<void> => {
  const { error } = await supabase
    .from("books_authors")
    .update({ bio })
    .eq("book_id", bookId)
    .eq("person_id", personId);

  if (error) {
    console.error("Error updating book author bio:", error);
    throw error;
  }
};

export const createPerson = async (
  personData: Omit<Person, "id" | "createdAt" | "updatedAt">
): Promise<Person> => {
  // First create the person
  const { data: newPerson, error: personError } = await supabase
    .from("people")
    .insert({
      name: personData.name,
      email: personData.email,
      bio: personData.bio,
      avatar_url: personData.avatarUrl,
    })
    .select()
    .single();

  if (personError) {
    console.error("Error creating person:", personError);
    throw personError;
  }

  // Then add their roles
  if (personData.roles && personData.roles.length > 0) {
    const roleEntries = personData.roles.map((role) => ({
      person_id: newPerson.id,
      role_id: role.id,
    }));

    const { error: rolesError } = await supabase
      .from("people_to_roles")
      .insert(roleEntries);

    if (rolesError) {
      console.error("Error adding roles to person:", rolesError);
      throw rolesError;
    }
  }

  // Return the full person object
  return {
    id: newPerson.id,
    name: newPerson.name,
    email: newPerson.email,
    bio: newPerson.bio,
    avatarUrl: newPerson.avatar_url,
    roles: personData.roles || [],
    createdAt: newPerson.created_at,
    updatedAt: newPerson.updated_at,
  };
};

export const updatePerson = async (
  id: number,
  updates: Partial<Person>
): Promise<Person> => {
  // Update the person record
  type PersonDbFields = {
    name?: string;
    email?: string;
    bio?: string;
    avatar_url?: string;
  };

  const personUpdates: PersonDbFields = {};
  if (updates.name !== undefined) personUpdates.name = updates.name;
  if (updates.email !== undefined) personUpdates.email = updates.email;
  if (updates.bio !== undefined) personUpdates.bio = updates.bio;
  if (updates.avatarUrl !== undefined)
    personUpdates.avatar_url = updates.avatarUrl;

  if (Object.keys(personUpdates).length > 0) {
    const { error: updateError } = await supabase
      .from("people")
      .update(personUpdates)
      .eq("id", id);

    if (updateError) {
      console.error("Error updating person:", updateError);
      throw updateError;
    }
  }

  // Update roles if provided
  if (updates.roles) {
    // First delete existing roles
    const { error: deleteError } = await supabase
      .from("people_to_roles")
      .delete()
      .eq("person_id", id);

    if (deleteError) {
      console.error("Error removing existing roles:", deleteError);
      throw deleteError;
    }

    // Then add the new roles
    if (updates.roles.length > 0) {
      const roleEntries = updates.roles.map((role) => ({
        person_id: id,
        role_id: role.id,
      }));

      const { error: insertError } = await supabase
        .from("people_to_roles")
        .insert(roleEntries);

      if (insertError) {
        console.error("Error adding new roles:", insertError);
        throw insertError;
      }
    }
  }

  // Fetch the updated person to return
  return fetchPerson(id);
};

export const deletePerson = async (id: number): Promise<void> => {
  const { error } = await supabase.from("people").delete().eq("id", id);

  if (error) {
    console.error("Error deleting person:", error);
    throw error;
  }
};

/**
 * Fetches total count of people regardless of any filters
 * @returns The total number of people in the database
 */
export const fetchTotalPeopleCount = async (): Promise<number> => {
  const { count, error } = await supabase
    .from("people")
    .select("id", { count: "exact", head: true });

  if (error) {
    console.error("Error fetching total people count:", error);
    throw error;
  }

  return count || 0;
};

/**
 * Checks if a person with the given name already exists in the database
 * @param name The name to check for
 * @param excludeId Optional ID to exclude from the check (useful for updates)
 * @returns True if the name already exists, false otherwise
 */
export const checkPersonNameExists = async (
  name: string,
  excludeId?: number
): Promise<boolean> => {
  let query = supabase.from("people").select("id").ilike("name", name.trim());

  // If we're updating a person, exclude their ID from the check
  if (excludeId) {
    query = query.neq("id", excludeId);
  }

  const { data, error } = await query;

  if (error) {
    console.error("Error checking person name:", error);
    throw error;
  }

  // If data exists and has length > 0, the name already exists
  return !!data && data.length > 0;
};
